import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { ZerosixService } from '@core/services/zerosix/zerosix.service';
import { iif } from 'rxjs';
import { ZerosixError } from '@core/models/zerosix.model';
import { ZEROSIX_ERRORS } from '@config/values/zerosix-errors.values';

@Component({
  selector: 'app-zerosix-phone-number-step',
  templateUrl: './zerosix-phone-number-step.component.html',
})
export class ZerosixPhoneNumberStepComponent extends DestroyerBase implements OnInit {
  @Input() phone = '';

  @Output() onClose = new EventEmitter<Event>();
  @Output() setPhone = new EventEmitter<string>();
  @Output() goToNextStep = new EventEmitter<void>();
  @Output() goToAuthenticationStep = new EventEmitter<void>();
  @Output() goToAccountNotFoundStep = new EventEmitter<void>();
  @Output() goToKioskAccountNotFoundStep = new EventEmitter<void>();

  @Input() accentColor?: ContentBuilderFieldColors;

  form: UntypedFormGroup;
  isValidating = false;
  errorMessage?: string;

  kioskParameters = this.settingsSelector.kiosk;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly zerosixService: ZerosixService,
    private readonly settingsSelector: SettingsStoreSelector,
  ) {
    super();
    this.form = this.formBuilder.group({
      ['phone']: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.form.setValue({ phone: this.phone ?? '' });
  }

  onRegister(): void {
    const phoneNumber = this.form.controls['phone']?.value;

    if (this.form.invalid || this.isValidating || !phoneNumber) {
      return;
    }
    this.isValidating = true;
    this.errorMessage = undefined;
    this.zerosixService
      .isZerosixOtpIdentificationRequired$()
      .pipe(
        switchMap(isOtpRequired => {
          return iif(
            () => isOtpRequired,
            this.zerosixService.sendOtp$(phoneNumber),
            this.zerosixService.attachUserByPhone$({
              phoneNumber,
            }),
          );
        }),
        takeUntil(this._destroyerRef),
        tap(response => {
          if (response) {
            this.setPhone.next(phoneNumber);
            this.goToNextStep.next();
          } else {
            this.goToAuthenticationStep.next();
          }
        }),
      )
      .subscribe({
        error: error => {
          this.handleErrorResponse(error.error, phoneNumber);
        },
        complete: () => (this.isValidating = false),
      });
  }

  onKeypadNumberClick(number: number): void {
    const currentValue = this.form.controls.phone.value;
    this.form.controls.phone.setValue(currentValue + number.toString());
  }

  onKeypadDeleteClick(): void {
    const currentValue = this.form.controls.phone.value;
    this.form.controls.phone.setValue(currentValue.slice(0, -1)); // Supprime le dernier caractère
  }

  private handleErrorResponse(error: ZerosixError, phone: string): void {
    if (error.class === 'ZerosixException') {
      if (error.code === 'ZEROSIX_MEMBER_NOT_FOUND') {
        this.setPhone.next(phone);
        this.kioskParameters.isKioskLayout
          ? this.goToKioskAccountNotFoundStep.next()
          : this.goToAccountNotFoundStep.next();
        return;
      }
      this.errorMessage = ZEROSIX_ERRORS[error.code];
      return;
    }
    this.errorMessage = 'errors.generic-error';
  }
}
